/* custom-scent-quiz.js — decision-tree product finder. Portable: no theme JS dependencies.
   Flow: one question per step → best-matching result block (score = number of matched key:value pairs,
   must reach min_score; ties → first block) → optional variant pick from an answer → add to cart via the
   Cart AJAX API → open the cart (Dawn <cart-drawer>, custom-cart-drawer module, or /cart). */
(() => {
  class CustomScentQuiz extends HTMLElement {
    connectedCallback() {
      this.form = this.querySelector('[data-quiz-form]');
      this.steps = [...this.querySelectorAll('[data-quiz-step]')];
      this.results = JSON.parse(this.querySelector('[data-quiz-results]').textContent || '[]');
      this.t = JSON.parse(this.querySelector('[data-quiz-strings]').textContent || '{}');
      this.card = this.closest('.custom-quiz__panel').querySelector('[data-quiz-result]');
      this.next = this.querySelector('[data-quiz-next]');
      this.back = this.querySelector('[data-quiz-back]');
      this.restart = this.querySelector('[data-quiz-restart]');
      this.error = this.querySelector('[data-quiz-error]');
      this.progress = this.querySelector('[data-quiz-progress]');
      this.index = 0;
      this.next.addEventListener('click', () => this.advance());
      this.back.addEventListener('click', () => this.go(this.index - 1));
      this.restart.addEventListener('click', () => this.reset());
      this.form.addEventListener('change', () => { this.error.hidden = true; });
      this.form.addEventListener('keydown', (e) => { if (e.key === 'Enter') { e.preventDefault(); this.advance(); } });
      this.card.querySelector('[data-result-add]').addEventListener('click', () => this.addToCart());
      const initial = JSON.parse(this.querySelector('[data-quiz-initial]')?.textContent || 'null');
      if (initial) this.current = { product: initial, variant: initial.variants.find((v) => v.available) || initial.variants[0] };
      this.go(0);
    }
    answers() {
      const out = {};
      this.steps.forEach((s) => { const c = s.querySelector('input:checked'); if (c) out[s.dataset.key] = c.value; });
      return out;
    }
    go(i) {
      this.index = Math.max(0, Math.min(i, this.steps.length - 1));
      this.steps.forEach((s, n) => { s.hidden = n !== this.index; });
      this.back.hidden = this.index === 0;
      this.progress.textContent = `${this.index + 1} / ${this.steps.length}`;
      this.error.hidden = true;
      const legend = this.steps[this.index].querySelector('legend');
      if (legend && document.activeElement !== document.body) legend.focus({ preventScroll: true });
    }
    advance() {
      const step = this.steps[this.index];
      if (!step.querySelector('input:checked')) { this.error.hidden = false; step.querySelector('input').focus(); return; }
      if (this.index < this.steps.length - 1) return this.go(this.index + 1);
      this.finish();
    }
    pick() {
      const a = this.answers();
      let best = null, bestScore = -1;
      this.results.forEach((r) => {
        const pairs = (r.match || '').split(',').map((p) => p.trim()).filter(Boolean);
        const score = pairs.reduce((n, p) => { const [k, v] = p.split(':').map((x) => x.trim()); return n + (a[k] === v ? 1 : 0); }, 0);
        if (score >= (r.min_score || 0) && score > bestScore) { best = r; bestScore = score; }
      });
      if (!best) best = this.results[0];
      if (!best) return null;
      let variant = best.variants.find((v) => v.available) || best.variants[0];
      if (best.variant_key && a[best.variant_key]) {
        const want = a[best.variant_key].replace(/[״"]/g, '');
        const hit = best.variants.find((v) => v.options.some((o) => o.replace(/[״"]/g, '') === want) || v.title.replace(/[״"]/g, '') === want);
        if (hit) variant = hit;
      }
      return { product: best, variant };
    }
    finish() {
      const res = this.pick();
      if (!res) return;
      this.current = res;
      const c = this.card, p = res.product, v = res.variant;
      c.querySelector('[data-result-badge]').textContent = p.badge || '';
      c.querySelector('[data-result-heading]').textContent = this.t.result_heading || '';
      c.querySelector('[data-result-image]').src = p.image || '';
      c.querySelector('[data-result-image]').alt = p.title;
      c.querySelector('[data-result-link]').href = p.url;
      c.querySelector('[data-result-title-link]').href = p.url;
      c.querySelector('[data-result-title]').textContent = p.title;
      const meta = [p.type]; if (p.variants.length > 1 && v.title !== 'Default Title') meta.push(`${this.t.variant_label || ''} ${v.title}`.trim());
      c.querySelector('[data-result-meta]').textContent = meta.filter(Boolean).join(' · ');
      const note = c.querySelector('[data-result-note]'); note.textContent = p.note || ''; note.hidden = !p.note;
      c.querySelector('[data-result-price]').textContent = v.price || p.price;
      const btn = c.querySelector('[data-result-add]'); btn.textContent = this.t.add_label; btn.disabled = !v.available;
      c.querySelector('[data-result-status]').textContent = '';
      c.hidden = false;
      this.form.querySelectorAll('[data-quiz-step]').forEach((s) => (s.hidden = true));
      this.next.hidden = true; this.back.hidden = true; this.restart.hidden = false; this.progress.textContent = '';
      c.querySelector('h3 a').focus({ preventScroll: true });
      c.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
      if (this.dataset.autoAdd === 'true' && v.available) this.addToCart();
    }
    reset() {
      this.form.reset(); this.card.hidden = true; this.next.hidden = false; this.restart.hidden = true; this.go(0);
      this.current = null;
    }
    async addToCart() {
      const { variant } = this.current || {}; if (!variant) return;
      const btn = this.card.querySelector('[data-result-add]'); const status = this.card.querySelector('[data-result-status]');
      btn.disabled = true; btn.setAttribute('aria-busy', 'true');
      const drawer = document.querySelector('cart-drawer');
      const body = { items: [{ id: variant.id, quantity: 1 }] };
      if (drawer) body.sections = 'cart-drawer,cart-icon-bubble';
      try {
        const r = await fetch(this.dataset.addUrl || '/cart/add.js', { method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' }, body: JSON.stringify(body) });
        const json = await r.json();
        if (!r.ok || json.status) throw new Error(json.description || json.message || 'error');
        btn.textContent = this.t.added_label; status.textContent = this.t.result_note || '';
        document.dispatchEvent(new CustomEvent('custom-cart:updated', { detail: { source: 'quiz', variantId: variant.id } }));
        if (this.dataset.openCart === 'true') {
          if (drawer && typeof drawer.renderContents === 'function') { drawer.renderContents(json); if (typeof drawer.open === 'function') drawer.open(); }
          else if (document.querySelector('custom-cart-drawer')) document.dispatchEvent(new CustomEvent('custom-cart:open'));
          else window.location.href = this.dataset.cartUrl || '/cart';
        }
      } catch (e) {
        status.textContent = this.t.error_label || e.message; btn.textContent = this.t.add_label;
      } finally { btn.disabled = false; btn.removeAttribute('aria-busy'); }
    }
  }
  if (!customElements.get('custom-scent-quiz')) customElements.define('custom-scent-quiz', CustomScentQuiz);
})();
